/**
 * @file Contains address validation rules.
 */
import { Province } from '@/constructs/provinces/Province';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { msg, msgf } from '@/services/isomorphic/I18NService';
import { isPOBoxLine } from '@/utils/string-utils';
import type { IValidationResult, Validation } from '../validatable';
import { checkout_shipping_errors_cannotShipToPOBox } from "@/lang/__generated__/ahnu/checkout_shipping_errors_cannotShipToPOBox";
import { forms_error_isState } from "@/lang/__generated__/ahnu/forms_error_isState";

/**
 * Validates that the input of a state to make sure it matches the sites state
 * enum.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const isState: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  if (!Object.keys(Province).includes(value)) {
    return {
      isValid: false,
      errorMessage: msgf(forms_error_isState, { name: name.toLowerCase() })
    };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Checks if a given address line is valid.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const validAddressLine: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  // Step 1: Check if the line is a PO Box and fail if shipping to PO Boxes is
  // disabled.
  const config = ConfigurationService.getConfig('checkout');
  const allowShippingToPOBoxes = config.getSetting(
    'allowShippingToPOBoxes'
  ).value;

  if (!allowShippingToPOBoxes && isPOBoxLine(value)) {
    return {
      isValid: false,
      errorMessage: msg(checkout_shipping_errors_cannotShipToPOBox)
    };
  }

  return { isValid: true, errorMessage: null };
};
